<template>
  <div id="fieldcustomeditor" class="col-sm-12">
    <input
      v-if="!element"
      ref="hiddenFieldInput"
      type="hidden"
      :name="name"
      data-testid="dynamic-form-value-input"
    />

    <div v-if="customFields != null">
      <div
        v-for="(field, index) in customFields"
        :key="index"
        :class="['form-group']"
        data-testid="field-item"
      >
        <label
          class="col-sm-2 control-label input-sm"
          data-testid="field-item-label"
          >{{ String(field.label ?? "").trim() || field.key }}</label
        >
        <div class="col-sm-9">
          <input
            v-model="field.value"
            type="text"
            :class="['form-control', 'input-sm', 'context_var_autocomplete']"
            size="100"
            @change="changeField()"
            :data-testid="'field-input-' + index"
          />
        </div>
        <div class="col-sm-1">
          <span
            class="btn btn-xs btn-default"
            :title="$t('message_delete')"
            @click="removeField(field)"
            data-testid="remove-field-button"
          >
            <i class="glyphicon glyphicon-remove"></i
          ></span>
        </div>
        <div v-if="field.desc" class="col-sm-10 col-sm-offset-2 help-block">
          <div class="help-block" data-testid="field-description-help">
            {{ field.desc }}
          </div>
        </div>
      </div>
    </div>

    <btn
      type="primary"
      @click="openNewField()"
      data-testid="add-field-button"
      >{{ $t("message_addField") }}</btn
    >

    <modal
      id="modal-demo"
      ref="modal"
      v-model="modalAddField"
      title="Add Field"
      ok-text="Save"
      :backdrop="true"
      :dismiss-btn="true"
      :keyboard="true"
      cancel-text="Close"
      append-to-body
      data-testid="modal-title"
    >
      <div class="row" style="padding-left: 30px !important">
        <alert
          v-if="duplicate"
          type="warning"
          data-testid="duplicate-warning"
          ref="duplicateWarningRef"
          ><b>{{ $t("message_warning") }}</b>
          {{ $t("message_duplicated") }}.</alert
        >

        <div class="col-md-10">
          <div v-if="useOptions" class="form">
            <div :class="['form-data']">
              <label class="col-md-4">{{ $t("message_select") }}</label>
              <div class="col-md-8">
                <!--
                  append-to="self" keeps the dropdown overlay inside this
                  modal's stacking context. The default ("body") renders it at
                  z-index 1001, below the surrounding modal (1070), which makes
                  the options render but stay unclickable.
                -->
                <pt-select
                  v-model="selectedField"
                  :options="customOptions"
                  option-label="label"
                  :filter="true"
                  :placeholder="$t('message_select')"
                  append-to="self"
                  data-testid="multiselect"
                />
              </div>
            </div>
          </div>

          <div v-if="!useOptions" class="form">
            <div :class="['form-group']">
              <label
                class="col-md-4"
                :for="`${uid}-key-input`"
                data-testid="field-key-label"
                >{{ $t("message_fieldKey") }}</label
              >
              <div class="col-md-8">
                <input
                  :id="`${uid}-key-input`"
                  v-model="newField"
                  type="text"
                  :class="['form-control']"
                  :aria-describedby="`${uid}-key-help`"
                  data-testid="field-key-input"
                />
                <div
                  :id="`${uid}-key-help`"
                  class="help-block"
                  data-testid="field-key-help"
                >
                  {{ $t("message_fieldKeyHelp") }}
                </div>
              </div>
            </div>
            <div :class="['form-group']">
              <label
                class="col-md-4"
                :for="`${uid}-label-input`"
                data-testid="field-label-label"
                >{{ $t("message_fieldLabel") }}</label
              >
              <div class="col-md-8">
                <input
                  :id="`${uid}-label-input`"
                  v-model="newLabelField"
                  type="text"
                  :class="['form-control']"
                  :aria-describedby="`${uid}-label-help`"
                  data-testid="field-label-input"
                />
                <div
                  :id="`${uid}-label-help`"
                  class="help-block"
                  data-testid="field-label-help"
                >
                  {{ $t("message_fieldLabelHelp") }}
                </div>
              </div>
            </div>
          </div>

          <div class="form">
            <div :class="useOptions ? 'form-data' : 'form-group'">
              <label
                class="col-md-4"
                :for="`${uid}-description-input`"
                data-testid="field-description-label"
              >
                {{ $t("message_description") }}
              </label>
              <div class="col-md-8">
                <input
                  :id="`${uid}-description-input`"
                  v-model="newFieldDescription"
                  type="text"
                  :class="['form-control']"
                  :aria-describedby="`${uid}-description-help`"
                  data-testid="field-description-input"
                />
                <div
                  :id="`${uid}-description-help`"
                  class="help-block"
                  data-testid="new-field-description-help"
                >
                  {{ $t("message_fieldDescriptionHelp") }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div>
          <button
            type="button"
            class="btn btn-default reset_page_confirm"
            @click="modalAddField = false"
            data-testid="cancel-button"
          >
            {{ $t("message_cancel") }}
          </button>

          <button
            type="button"
            class="btn btn-cta reset_page_confirm"
            @click="addField()"
            data-testid="confirm-add-field-button"
          >
            {{ $t("message_add") }}
          </button>
        </div>
      </template>
    </modal>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Btn, Alert, Modal } from "uiv";
import PtSelect from "../primeVue/PtSelect/PtSelect.vue";

interface CustomField {
  key?: string;
  label?: string;
  value?: string;
  desc?: string;
}

export default defineComponent({
  name: "DynamicFormPluginProp",
  components: {
    PtSelect,
    Btn,
    Alert,
    Modal,
  },
  props: {
    fields: {
      type: String,
      required: true,
    },
    options: {
      type: String,
      required: false,
    },
    hasOptions: {
      type: String,
      required: true,
    },
    element: {
      type: String,
      required: false,
      default: "",
    },
    name: {
      type: String,
      required: true,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      // Random rather than useId(): useId() is only unique within one Vue app,
      // and pages mount this component from many separate apps and bundles.
      uid: `dynamic-form-${Math.random().toString(36).slice(2)}`,
      customFields: [] as CustomField[],
      customOptions: [] as any[],
      useOptions: false,
      modalAddField: false,
      duplicate: false,
      newField: "",
      newLabelField: "",
      newFieldDescription: "",
      selectedField: { value: "", label: "" },
    };
  },
  watch: {
    fields(newFields: string) {
      // Keep local state in sync if the prop changes after mount (e.g. the
      // parent round-trips the value through its own v-model chain). Skip
      // when the incoming value already matches what we just emitted
      // ourselves, to avoid fighting with in-flight edits.
      if (newFields === JSON.stringify(this.customFields)) {
        return;
      }
      this.syncFieldsFromProp(newFields);
    },
  },
  mounted() {
    if (this.hasOptions === "true") {
      this.useOptions = true;
    }
    this.syncFieldsFromProp(this.fields);

    if (
      this.useOptions &&
      this.options !== null &&
      this.options !== undefined &&
      this.options !== ""
    ) {
      const optionsObject = JSON.parse(this.options!);
      const options = Object.keys(optionsObject).map((key: any) => {
        const data = optionsObject[key];
        return { value: key, label: data };
      });
      this.customOptions = options;
    }
  },
  beforeUnmount() {
    this.customFields = null as any;
  },
  methods: {
    syncFieldsFromProp(fields: string) {
      if (fields == null || fields === "") {
        // Clearing the prop must clear the list too, otherwise the previous
        // fields stay on screen after the parent resets the value.
        this.customFields = [];
        return;
      }
      const customFieldsObject = JSON.parse(fields);
      if (customFieldsObject != null) {
        const parsedFields = Object.keys(customFieldsObject).map((key: any) => {
          const value = customFieldsObject[key];
          if (value.desc == null) {
            value.desc = this.$t("message_fieldKeyDescription", [value.key]);
          }
          return value;
        });
        this.customFields = parsedFields;
      }
    },
    openNewField() {
      this.duplicate = false;
      this.modalAddField = true;
    },
    addField() {
      this.duplicate = false;
      // Nothing chosen from the options list: keep the modal open, add nothing.
      if (this.useOptions && !this.selectedField?.value) return;

      const key =
        (this.useOptions ? this.selectedField?.value : this.newField) ?? "";

      const typed = this.newFieldDescription;
      const desc =
        typed == ""
          ? this.$t("message_fieldKeyOnlyDescription", [key])
          : this.$t("message_fieldKeyAppendedDescription", [typed, key]);

      // Free-text label falls back to the key, per message_fieldLabelHelp.
      const field: CustomField = this.useOptions
        ? { key, label: this.selectedField?.label, desc }
        : { key, label: this.newLabelField.trim() || key, value: "", desc };
      let exists = false;
      this.customFields.forEach((row: any) => {
        if (field.key === row.key) {
          exists = true;
        }
      });

      if (!exists) {
        this.customFields.push(field);
        this.newField = "";
        this.newLabelField = "";
        this.newFieldDescription = "";
        this.modalAddField = false;
        this.refreshPlugin();
      } else {
        this.duplicate = true;
      }
    },
    removeField(row: any) {
      const fields = [] as CustomField[];
      this.customFields.forEach((field: CustomField) => {
        if (field.key !== row.key) {
          fields.push(field);
        }
      });
      this.customFields = fields;
      this.refreshPlugin();
    },
    changeField() {
      this.refreshPlugin();
    },
    /**
     * Synchronizes generated JSON with the component-owned submitted input.
     * Legacy GSP callers receive the value through the parent event handler.
     */
    refreshPlugin(): void {
      const fieldsJson = JSON.stringify(this.customFields);
      const inputElement = this.$refs.hiddenFieldInput as
        HTMLInputElement | undefined;
      if (inputElement) {
        inputElement.value = fieldsJson;
      }
      this.$emit("update:modelValue", fieldsJson);
    },
  },
});
</script>
<style scoped>
.form-data {
  padding-bottom: 30px;
  margin-bottom: 20px;
}
</style>
